import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { motion } from "motion/react";
import { useBackToClose } from "../../utils/backToClose";

// Touch devices: long-pressing a message opens this sheet from the bottom of the screen with quick
// reactions and the message actions (the hover toolbar's job on desktop).
function MessageActionSheet({ preview, reactions, onReact, actions, onClose }) {
  useBackToClose(true, onClose);
  // Lifting the finger that long-pressed can fire a click on the backdrop that just appeared under it;
  // only a tap that also started on the backdrop closes the sheet.
  const backdropPressRef = useRef(false);
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-[85] flex items-end" data-chat-action-sheet>
      <motion.div
        className="absolute inset-0 bg-black/40"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.15 }}
        onPointerDown={() => {
          backdropPressRef.current = true;
        }}
        onClick={() => {
          if (backdropPressRef.current) onClose();
          backdropPressRef.current = false;
        }}
        aria-hidden="true"
      />
      <motion.div
        role="dialog"
        aria-modal="true"
        aria-label="Message actions"
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        transition={{ type: "tween", ease: [0.22, 1, 0.36, 1], duration: 0.25 }}
        className="relative w-full rounded-t-2xl bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 shadow-2xl pb-[max(0.75rem,env(safe-area-inset-bottom))]"
      >
        <div className="mx-auto mt-2 mb-1 h-1 w-10 rounded-full bg-gray-300 dark:bg-gray-600" aria-hidden="true" />
        {preview && <p className="mx-4 mt-1 mb-2 line-clamp-2 break-words text-xs text-gray-500 dark:text-gray-400">{preview}</p>}
        {reactions?.length > 0 && (
          <div className="mx-3 mb-2 flex justify-between rounded-full bg-gray-100 dark:bg-gray-800 px-2 py-1.5">
            {reactions.map((emoji) => (
              <button
                key={emoji}
                type="button"
                onClick={() => onReact(emoji)}
                className="h-11 w-11 rounded-full text-2xl active:scale-90 active:bg-gray-200 dark:active:bg-gray-700 transition-transform"
                aria-label={`React ${emoji}`}
              >
                {emoji}
              </button>
            ))}
          </div>
        )}
        <ul className="py-1">
          {actions.map((action) => (
            <li key={action.label}>
              <button
                type="button"
                onClick={action.onClick}
                className={`flex w-full items-center gap-4 px-5 py-3.5 text-left text-[15px] active:bg-gray-100 dark:active:bg-gray-800 ${
                  action.danger ? "text-red-600" : ""
                }`}
              >
                <span className={action.danger ? "text-red-600" : "text-gray-500 dark:text-gray-400"}>{action.icon}</span>
                {action.label}
              </button>
            </li>
          ))}
        </ul>
      </motion.div>
    </div>,
    document.body
  );
}

export default MessageActionSheet;
